import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { RecordsPage } from '@/components/records/RecordsPage';
import { AnalyticsPage } from '@/components/analytics/AnalyticsPage';
import { LoginPage } from '@/pages/LoginPage';
import { AdminPage } from '@/pages/AdminPage';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { UserMenu } from '@/components/UserMenu';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useTable, TableProvider } from '@/context/TableContext';
import { FieldVisibilityProvider } from '@/context/FieldVisibilityContext';
import { checkConnection } from '@/services/supabase';
import { cn } from '@/utils';
import { WifiOff } from 'lucide-react';

type Page = 'records' | 'analytics' | 'admin';

function DashboardLayout() {
  const { resolvedTheme } = useTheme();
  const { isAdmin, profile } = useAuth();
  const { currentTable, setCurrentTable, availableTables, loadingTables } = useTable();
  const [currentPage, setCurrentPage] = useState<Page>('records');
  const [connected, setConnected] = useState(true);
  const [loading, setLoading] = useState(true);
  
  // Use a ref to track if we've already auto-selected the table
  const hasAutoSelected = useRef(false);

  // Initialize table from profile on first load
  useEffect(() => {
    // Only run when:
    // 1. We haven't auto-selected yet
    // 2. Tables are loaded
    // 3. Profile is loaded (has a table value)
    // 4. Available tables is populated
    if (!hasAutoSelected.current && !loadingTables && availableTables.length > 0 && profile?.table) {
      // Check if the table from profile exists in available tables (case-insensitive)
      const profileTableTrimmed = profile.table.trim();
      const tableExists = availableTables.some(t => 
        t.name.toLowerCase() === profileTableTrimmed.toLowerCase()
      );
      
      if (tableExists) {
        // Find the exact table name from availableTables
        const matchedTable = availableTables.find(t => 
          t.name.toLowerCase() === profileTableTrimmed.toLowerCase()
        );
        if (matchedTable) {
          setCurrentTable(matchedTable.name);
          hasAutoSelected.current = true;
        }
      }
    }
  }, [profile?.table, loadingTables, availableTables.length, setCurrentTable]);

  // Check connection on load
  useEffect(() => {
    const check = async () => {
      const isConnected = await checkConnection();
      setConnected(isConnected);
      setLoading(false);
    };
    check();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className={cn(
      'min-h-screen flex flex-col transition-colors duration-300',
      resolvedTheme === 'dark' ? 'dark bg-slate-950' : 'bg-slate-50'
    )}>
      {/* Connection Banner */}
      {!connected && (
        <div className="bg-red-500 text-white px-4 py-2 flex items-center justify-center gap-2 animate-pulse-soft">
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">Connection lost - Attempting to reconnect...</span>
        </div>
      )}

      {/* Header */}
      <Header
        onRefresh={handleRefresh}
        loading={loading}
        connected={connected}
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
        isAdmin={isAdmin}
        profile={profile}
        rightContent={<UserMenu />}
      />

      {/* Main Content */}
      <main className="flex-1 max-w-[1920px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {currentPage === 'records' && (
          <RecordsPage tableName={currentTable} />
        )}
        {currentPage === 'analytics' && (
          <AnalyticsPage tableName={currentTable} />
        )}
        {currentPage === 'admin' && isAdmin && (
          <AdminPage />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t mt-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex flex-col items-center sm:items-start gap-1">
            <p className="font-medium text-foreground">Built by CodeCraft Innovations WLL</p>
            <p className="text-xs">Bahrain • 2026</p>
          </div>
          <a 
            href="mailto:connect@codecraftbase.com" 
            className="hover:text-primary transition-colors"
          >
            connect@codecraftbase.com
          </a>
        </div>
      </footer>
    </div>
  );
}

function App() {
  const { permissions, isAdmin } = useAuth();

  return (
    <BrowserRouter>
      {/* Pass permissions to TableProvider to filter tables based on user access */}
      <TableProvider permissions={isAdmin ? [] : permissions}>
        <FieldVisibilityWrapper />
      </TableProvider>
    </BrowserRouter>
  );
}

// Wrapper component to sync FieldVisibilityProvider with TableContext
function FieldVisibilityWrapper() {
  const { currentTable } = useTable();
  
  return (
    <FieldVisibilityProvider tableName={currentTable}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </FieldVisibilityProvider>
  );
}

export default App;
